import React from 'react';
import { Col, Row } from '@/lib/utils';
import { DemiLightText, MediumText, RegularText } from '@/lib/Text';
import SvgIcon from '@/components/SvgIcon';

/** 구간 입력용 숫자 Input (매출·회차 공용) */
export const BracketInput = ({
    value,
    onChange,
    unit,
    placeholder,
    width = 140,
}: {
    value: number | null | undefined;
    onChange: (val: number | null) => void;
    unit?: string;
    placeholder?: string;
    width?: number;
}) => {
    return (
        <div
            className={`flex items-center  h-[43px] border border-solid border-[#E6E8EA]  rounded-[10px] bg-white pl-[18px] cursor-text transition-colors duration-300 ease-in-out`}
            style={{ width }}
        >
            <input
                type="text"
                value={value ?? ''}
                onChange={(e) => {
                    let val = e.target.value.replace(/[^0-9]/g, '');
                    if (val.length > 1 && val[0] === '0') val = val.slice(1);
                    onChange(val ? Number(val) : null);
                }}
                onClick={(e) => {
                    const target = e.target as HTMLInputElement;
                    requestAnimationFrame(() => {
                        const length = target.value.length;
                        target.setSelectionRange(length, length);
                    });
                }}
                onFocus={(e) => {
                    const target = e.target as HTMLInputElement;
                    requestAnimationFrame(() => {
                        const length = target.value.length;
                        target.setSelectionRange(length, length);
                    });
                }}
                placeholder={placeholder}
                className="flex flex-0.8 text-start w-full items-center outline-none  font-noto-regular text-[17px] text-[#2D3642]"
            />
            {unit && (
                <DemiLightText
                    className="flex flex-0.2 items-center w-[36px] min-w-[36px] justify-end   mr-[20px]"
                    color="#6B7280"
                >
                    {unit}
                </DemiLightText>
            )}
        </div>
    );
};

/** 구간 삭제 버튼 */
export const RemoveButton = ({ onClick }: { onClick: () => void }) => (
    <div
        onClick={onClick}
        className="w-[24px] h-[24px] rounded-full bg-[#B4B4B6] flex items-center justify-center cursor-pointer ml-[12px]"
    >
        <SvgIcon name="SvgX" size={12} stroke="white" strokeWidth={2} />
    </div>
);

/** 코치 리스트 패널 (왼쪽 사이드바) */
export const CoachListPanel = ({
    coaches,
    selectedCoachId,
    onSelect,
    isCoachModified,
    isCoachWarned,
}: {
    coaches: { coachId: string; coachName: string }[];
    selectedCoachId: string | null;
    onSelect: (coachId: string) => void;
    isCoachModified?: (coachId: string) => boolean;
    /** 주의 표시(주황 점)를 붙일 강사 — 예: IN 요율이 0원이 될 수 있는데 시간과 상관없이 IN 으로 계산하는 수업이 있음. 없으면 아무것도 안 그린다. */
    isCoachWarned?: (coachId: string) => boolean;
}) => (
    <Col className="w-[164px] min-w-[164px] mr-[20px] gap-[10px]    overflow-y-scroll">
        <DemiLightText fontSize={14} lineHeight={20} color="#6B7280" className="mb-[12px]">
            강사 리스트
        </DemiLightText>
        {coaches.map((coach) => {
            const modified = isCoachModified ? isCoachModified(coach.coachId) : false;
            const warned = isCoachWarned ? isCoachWarned(coach.coachId) : false;
            return (
                <div
                    key={coach.coachId}
                    className={`flex flex-row items-center px-[16px] py-[10px] cursor-pointer mb-[2px] rounded-[12px] transition-colors justify-between ${
                        selectedCoachId === coach.coachId ? 'bg-[#F2F4F6]' : 'hover:bg-[#F2F4F6]'
                    }`}
                    onClick={() => onSelect(coach.coachId)}
                >
                    <Row className="items-center">
                        {selectedCoachId === coach.coachId ? (
                            <MediumText fontSize={18} lineHeight={24} color="#3D73DD">
                                {coach.coachName}
                            </MediumText>
                        ) : (
                            <RegularText fontSize={18} lineHeight={24} color="#3D4244">
                                {coach.coachName}
                            </RegularText>
                        )}
                        {modified && <div className="w-[6px] h-[6px] bg-[#3D73DD] rounded-full ml-[6px]" />}
                        {warned && (
                            <div
                                aria-label="IN 요율 확인 필요"
                                title="IN 요율이 0원이 될 수 있어요"
                                className="w-[6px] h-[6px] bg-[#F59E0B] rounded-full ml-[6px]"
                            />
                        )}
                    </Row>
                </div>
            );
        })}
    </Col>
);
